import React from 'react';

interface LoadingSpinnerProps {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  variant?: 'default' | 'glass' | 'minimal' | 'pulse' | 'ai' | 'neural' | 'quantum';
  className?: string;
  text?: string;
  showText?: boolean;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ 
  size = 'md',
  variant = 'ai',
  className = '',
  text = 'AI Processing...',
  showText = false
}) => {
  const sizeClasses = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-12 h-12',
    '2xl': 'w-16 h-16'
  };

  const textSizeClasses = {
    xs: 'text-xs',
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl',
    '2xl': 'text-2xl'
  };

  const renderSpinner = () => {
    switch (variant) {
      case 'glass':
        return (
          <div className="relative">
            {/* Outer glow ring */}
            <div 
              className={`${sizeClasses[size]} rounded-full animate-spin`}
              style={{
                background: 'conic-gradient(from 0deg, transparent 0deg, rgba(0, 212, 255, 0.2) 45deg, rgba(124, 58, 237, 0.3) 90deg, rgba(0, 212, 255, 0.2) 135deg, transparent 180deg)',
                filter: 'blur(1px)'
              }}
            />
            {/* Main spinner */}
            <div 
              className={`${sizeClasses[size]} absolute inset-0 rounded-full animate-spin border-2 border-transparent`}
              style={{
                background: 'conic-gradient(from 0deg, transparent 0deg, rgba(0, 212, 255, 0.8) 45deg, rgba(124, 58, 237, 0.9) 90deg, rgba(0, 212, 255, 0.8) 135deg, transparent 180deg)',
                mask: 'radial-gradient(circle, transparent 60%, black 70%)'
              }}
            />
            {/* Inner glass effect */}
            <div 
              className={`${sizeClasses[size]} absolute inset-0 rounded-full bg-glass-surface backdrop-blur-sm border border-glass-border`}
              style={{
                background: 'linear-gradient(135deg, rgba(45, 45, 45, 0.3) 0%, rgba(26, 26, 26, 0.5) 100%)'
              }}
            />
          </div>
        );

      case 'minimal':
        return (
          <div 
            className={`${sizeClasses[size]} rounded-full border-2 border-glass-border border-t-primary-500 animate-spin`}
          />
        );

      case 'pulse':
        return (
          <div className="relative">
            <div 
              className={`${sizeClasses[size]} rounded-full bg-primary-500 animate-pulse`}
              style={{
                boxShadow: '0 0 20px rgba(0, 212, 255, 0.5)'
              }}
            />
            <div 
              className={`${sizeClasses[size]} absolute inset-0 rounded-full bg-primary-500 animate-ping opacity-20`}
            />
          </div>
        );

      case 'ai':
        return (
          <div className={`${sizeClasses[size]} relative`}>
            <svg viewBox="0 0 100 100" className="w-full h-full">
              <defs>
                <linearGradient id="aiGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#00D4FF" />
                  <stop offset="100%" stopColor="#3B82F6" />
                </linearGradient>
              </defs>
              {/* Network connections */}
              <g stroke="url(#aiGradient)" fill="none" strokeWidth={2} opacity={0.9}>
                <path d="M20,50 C30,20 70,20 80,50 C70,80 30,80 20,50 Z" className="animate-neural-dash" />
                <path d="M20,50 L40,30 L60,30 L80,50 L60,70 L40,70 Z" className="animate-neural-dash" style={{ animationDelay: '0.2s' as unknown as undefined }} />
                <path d="M40,30 L60,70 M60,30 L40,70" className="animate-neural-dash" style={{ animationDelay: '0.4s' as unknown as undefined }} />
              </g>
              {/* Nodes */}
              <g fill="url(#aiGradient)">
                {[{x:20,y:50},{x:40,y:30},{x:60,y:30},{x:80,y:50},{x:60,y:70},{x:40,y:70},{x:50,y:50}].map((p,i)=> (
                  <circle key={i} cx={p.x} cy={p.y} r={i===6?3.5:2.5} className="neural-node" style={{ animationDelay: `${i*0.12}s` as unknown as undefined }} />
                ))}
              </g>
            </svg>
            {/* Soft glow */}
            <div className="absolute inset-0 rounded-full" style={{ boxShadow: '0 0 24px rgba(0,212,255,0.25), inset 0 0 8px rgba(59,130,246,0.2)' }} />
          </div>
        );

      case 'neural':
        return (
          <div className={`${sizeClasses[size]} relative`}>
            {/* Orbits */}
            <div className="absolute inset-0">
              <div className="absolute inset-0 rounded-full border border-purple-400/30" />
              <div className="absolute inset-3 rounded-full border border-purple-500/40 animate-orbit" />
              <div className="absolute inset-6 rounded-full border border-purple-400/30 animate-orbit" style={{ animationDirection: 'reverse' }} />
            </div>
            {/* Orbiting nodes */}
            <div className="absolute inset-0">
              <div className="absolute left-1/2 top-0 -translate-x-1/2">
                <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-purple-400 to-pink-500 neural-node" />
              </div>
              <div className="absolute right-0 top-1/2 -translate-y-1/2">
                <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-purple-400 to-pink-500 neural-node" style={{ animationDelay: '0.2s' }} />
              </div>
              <div className="absolute left-1/2 bottom-0 -translate-x-1/2">
                <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-purple-400 to-pink-500 neural-node" style={{ animationDelay: '0.4s' }} />
              </div>
              <div className="absolute left-0 top-1/2 -translate-y-1/2">
                <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-purple-400 to-pink-500 neural-node" style={{ animationDelay: '0.6s' }} />
              </div>
            </div>
            {/* Core */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-400 to-pink-500 neural-node" />
            </div>
          </div>
        );

      case 'quantum':
        return (
          <div className={`${sizeClasses[size]} relative`}>
            {/* Quantum particles */}
            <div className="absolute inset-0 animate-spin">
              <div className="w-full h-full border-2 border-emerald-400/30 rounded-full" />
            </div>
            <div className="absolute inset-0 animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}>
              <div className="w-3/4 h-3/4 border-2 border-emerald-500/50 rounded-full m-auto" />
            </div>
            <div className="absolute inset-0 animate-spin" style={{ animationDuration: '2s' }}>
              <div className="w-1/2 h-1/2 border-2 border-emerald-400 rounded-full m-auto" />
            </div>
            {/* Quantum core */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-1/4 h-1/4 rounded-full bg-gradient-to-r from-emerald-400 to-teal-500 animate-pulse" />
            </div>
          </div>
        );

      default:
        return (
          <div className="relative">
            {/* Outer ring */}
            <div 
              className={`${sizeClasses[size]} rounded-full border-2 border-glass-border animate-spin`}
            />
            {/* Inner spinning ring */}
            <div 
              className={`${sizeClasses[size]} absolute inset-0 rounded-full border-2 border-transparent border-t-primary-500 animate-spin`}
              style={{ animationDirection: 'reverse', animationDuration: '0.8s' }}
            />
          </div>
        );
    }
  };

  return (
    <div className={`flex flex-col items-center justify-center gap-3 ${className}`}>
      {renderSpinner()}
      {showText && text && (
        <p className={`${textSizeClasses[size]} text-text-muted font-medium animate-pulse`}>
          {text}
        </p>
      )}
    </div>
  );
};

// Additional specialized loading components
export const LoadingDots: React.FC<{ className?: string; size?: 'sm' | 'md' | 'lg' }> = ({ 
  className = '', 
  size = 'md' 
}) => {
  const dotSizes = {
    sm: 'w-1 h-1',
    md: 'w-2 h-2',
    lg: 'w-3 h-3'
  };

  return (
    <div className={`flex items-center space-x-1 ${className}`}>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className={`${dotSizes[size]} rounded-full bg-primary-500 animate-pulse`}
          style={{
            animationDelay: `${i * 0.2}s`,
            animationDuration: '1s'
          }}
        />
      ))}
    </div>
  );
};

export const LoadingBars: React.FC<{ className?: string; count?: number }> = ({ 
  className = '', 
  count = 4 
}) => {
  return (
    <div className={`flex items-center space-x-1 ${className}`}>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="w-1 h-6 bg-primary-500 rounded-full animate-pulse"
          style={{
            animationDelay: `${i * 0.1}s`,
            animationDuration: '1.2s'
          }}
        />
      ))}
    </div>
  );
};

export default LoadingSpinner;
